import React, { useContext, useMemo } from "react";
import { AuthContext, IntlContext, FlashContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { updateProfile } from "hooks/profile";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import Button from "ui/Button";
import ProfileFormFields from "./FormFields";
import { Role } from "types/user";
import blobToBase64 from "helpers/file/blobToBase64";
import { hasKey, isRecord } from "helpers/typing";

interface Props {
    onEditSubmitSuccess: () => void;
}

const ProfileCommonEdit: React.FC<Props> = ({ onEditSubmitSuccess }) => {
    const intl = useContext(IntlContext);
    const { account, currentRole } = useContext(AuthContext);
    const flashMessage = useContext(FlashContext);

    const onSubmit = async (values: Record<string, unknown>) => {
        try {
            if (
                isRecord(values) &&
                hasKey(values, "resources") &&
                isRecord(values.resources) &&
                hasKey(values.resources, "provider") &&
                isRecord(values.resources.provider) &&
                hasKey(values.resources.provider, "kbis") &&
                isRecord(values.resources.provider.kbis) &&
                hasKey(values.resources.provider.kbis, "content") &&
                values.resources.provider.kbis.content instanceof Blob
            ) {
                values.resources.provider.kbis.content = await blobToBase64(values.resources.provider.kbis.content);
            }

            await updateProfile(account!.user.id, values);
            flashMessage?.addSuccess(intl.formatMessage("profile.user.data.update"));
            onEditSubmitSuccess();
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    const initialValues = useMemo(() => {
        const data = {
            gender: account?.user.gender,
            last_name: account?.user.last_name,
            first_name: account?.user.first_name,
            birth_name: account?.user.birth_name,
            phone: account?.user.phone,
            address: account?.user.address,
            email: account?.user.email,
            is_without_france: '0',
            country_birth_code: account?.user.country_birth_code,
            department_birth_code: account?.user.department_birth_code,
            commune_code_birth: account?.user.commune_code_birth,
            city_birth: account?.user.city_birth,
            is_attach_company:
                currentRole === Role.CLIENT && account?.resources?.client[0]?.company_id ? "1" : "0",
            resources: {},
        };

        if (currentRole === Role.CLIENT) {
            data.resources = {
                client: {
                    company_id: account?.resources?.client[0]?.company_id,
                    company_name: account?.resources?.client[0].company_name,
                    code: account?.resources?.client[0].company_code,
                    has_pet: account?.resources?.client[0].has_pet ? "1" : "0",
                    pet_type: account?.resources?.client[0].pet_type,
                    services: account?.resources?.client[0].services,
                    comment: account?.resources?.client[0].comment,
                    birthday: account?.resources?.client[0].birthday,
                },
            };
        }

        if (currentRole === Role.PROVIDER) {
            data.resources = {
                provider: {
                    csp: account?.resources?.provider[0].csp,
                    kbis: account?.resources?.provider[0].kbis_id,
                    type: account?.resources?.provider[0].type,
                    siret: account?.resources?.provider[0].siret,
                    has_tva: account?.resources?.provider[0].has_tva === "1" ? "1" : "0",
                    tva_number: account?.resources?.provider[0].tva_number,
                    tva_rate: account?.resources?.provider[0].tva_rate ?? null,
                    birthday: account?.resources?.provider[0].birthday,
                    nir: account?.resources?.provider[0].nir,
                },
            };
        }

        return data;
    }, [
        account?.resources?.client,
        account?.resources?.provider,
        account?.user.address,
        account?.user.birth_name,
        account?.user.email,
        account?.user.first_name,
        account?.user.gender,
        account?.user.last_name,
        account?.user.phone,
        account?.user.country_birth_code,
        account?.user.department_birth_code,
        account?.user.commune_code_birth,
        account?.user.city_birth,
        currentRole
    ]);

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit} className="register__form">
                    <ProfileFormFields />
                    <div className="text-center margin-top--3 padding-top--3">
                        <Button className="btn btn--primary" type="submit" disabled={submitting}>
                            {intl.formatMessage("generic.action.save")}
                            {submitting ? <div className="loader-inner" /> : null}
                        </Button>
                        {currentRole === Role.PROVIDER && (
                            <div className="comment margin-top--2">
                                <span className="profile--provider_mandatory_field">Date de naissance</span> / <span className="profile--provider_mandatory_field">Ville de naissance</span> / <span className="profile--provider_mandatory_field">N° de sécurité sociale</span> : champs obligatoires pour activer la publication de vos services.
                            </div>
                        )}
                    </div>
                </form>
            )}
        />
    );
};

export default ProfileCommonEdit;
